<template>
    <modal :parent="configurationStore.appContainerSelector" @opened="selectorOpened" @closed="selectorClosed">
        <template #opener="{ open }">
            <text-field
                :name="NAME"
                :class="{ manual: manualWalletAddressEnabled }"
                :readonly="!manualWalletAddressEnabled"
                :value="currentWalletAddressDisplayValue"
                :focus="currentWalletAddressType === 'manual' && focusWalletAddressField"
                :validations="validations"
                :triggerValidation="triggerRequiredError"
                class="wallet-address-field"
                required
                @click.prevent="(event: PointerEvent) => clicked(event, open)"
                @keydown="quickSelectExistingAddress"
                @created="createdManualAddress"
                @updated="selectedManualAddress"
            >
                <template #label>
                    {{ t('widget.wallet-address-select.label') }}
                </template>
                <template #prepend>
                    <icon
                        v-show="!manualWalletAddressEnabled && !!currentWalletAddress"
                        :name="currentWalletAddress?.currency ?? ''"
                        :alt="currentWalletAddress?.currency ?? ''"
                        directory="currencies"
                    />
                </template>
                <template v-if="!!walletAddresses.length" #append>
                    <span ref="append" :class="{ background: manualWalletAddressEnabled }">
                        <caret-icon direction="down" />
                    </span>
                </template>
                <template #information v-if="manualWalletAddressEnabled && !!walletAddresses.length">
                    {{ t('widget.wallet-address-select.information.manual-wallet') }}
                </template>
                <template #required> {{ t('widget.field-error.required') }} </template>
                <template #wallet-address>
                    {{ t('widget.wallet-address-select.field-error.invalid-wallet-address', { crypto: currenciesStore.selectedCryptoCurrency.name }) }}
                </template>
            </text-field>
        </template>

        <template #header="{ close }">
            <view-header close-button @closed="closeWalletSelect(close)">
                {{ t('widget.wallet-address-select.modal.title') }}
            </view-header>
            <search-field :value="filter" :focus="focusSearchField" @updated="search" />
        </template>

        <template #default="{ close }">
            <p v-if="!walletAddresses.length">
                {{ t('widget.wallet-address-select.modal.empty-list') }}
            </p>

            <list-select
                v-else
                name="wallet-address-select"
                :selected="!!currentWalletAddress ? [currentWalletAddress.identifier] : []"
                :disabled="disabledWalletAddressIdentifiers"
                @updated="(data) => selectedExistingAddress(data, close)"
            >
                <template v-for="wallet of walletAddresses" #[wallet.identifier]>
                    <list-select-item :icon="wallet.currency" directory="currencies" :class="wallet.identifier">
                        {{ ellipsis(wallet.name, 35, 20, 9) }}
                        <template #sub>
                            {{
                                !disabledWalletAddressIdentifiers.includes(wallet.identifier)
                                    ? ellipsis(wallet.address, 20, 15)
                                    : t('widget.wallet-address-select.modal.wallet-address-unavailable')
                            }}
                        </template>
                    </list-select-item>
                </template>
            </list-select>
        </template>
    </modal>
</template>

<script lang="ts" setup>
import { useTranslations } from '@btcdirect/composables';
import { getWalletAddressValidation, isDisabled, isVerified } from '@btcdirect/functions';
import SearchField from '@btcdirect/shared/src/components/form/fields/search-field.vue';
import { ShowFieldErrorSymbol } from '@btcdirect/shared/src/components/types';
import { QuoteType } from '@btcdirect/shared/src/composables/api/quote/types';
import { ellipsis } from '@btcdirect/shared/src/composables/util/text';
import {
    CurrencyPair,
    WalletAddress,
    WalletAddressType,
    useClientInfoStore,
    useConfigurationStore,
    useCurrenciesStore,
    useWalletAddressesStore
} from '@btcdirect/stores';
import { Modal, TextField, ValidatedStringArrayFieldData, ValidatedStringFieldData, ValidationMethod } from 'out-of-the-vue';
import { Ref, computed, inject, onMounted, ref, watch } from 'vue';
import { CaretIcon, Icon } from '../icons';
import ListSelectItem from '../list-select/list-select-item.vue';
import ListSelect from '../list-select/list-select.vue';
import ViewHeader from '../view/view-header.vue';

const emit = defineEmits<{ (event: 'created' | 'updated', data: ValidatedStringFieldData): void }>();

const props = defineProps<{ quoteType: QuoteType; translationDomain: string }>();

const showFieldErrors = inject<Ref<boolean>>(ShowFieldErrorSymbol, ref<boolean>(false));

const currenciesStore = useCurrenciesStore();
const walletAddressesStore = useWalletAddressesStore();
const configurationStore = useConfigurationStore();
const clientInfoStore = useClientInfoStore();
const { t } = useTranslations();

const manualWalletAddressEnabled = computed<boolean>(() => clientInfoStore.client.walletVerificationRequired);

const NAME = 'walletAddress';

const currentWalletAddress = computed<WalletAddress>(() => walletAddressesStore.currentWalletAddress ?? null);
const currentWalletAddressType = computed<WalletAddressType>(() => walletAddressesStore.currentWalletAddressType ?? null);

const currentWalletAddressDisplayValue = computed<string>(() => {
    if (!currentWalletAddress.value) {
        return null;
    }

    if (!manualWalletAddressEnabled.value) {
        return currentWalletAddress.value.name ?? ellipsis(currentWalletAddress.value?.address, 20, 15);
    }

    return currentWalletAddressType.value === 'manual' ? currentWalletAddress.value.address : ellipsis(currentWalletAddress.value?.address, 25, 20);
});

const filter = ref<string>('');
const previousFilter = ref<string>('');
const search = (data: ValidatedStringFieldData): void => {
    filter.value = !!data.value ? data.value.trim() : null;
};

const walletAddresses = computed<WalletAddress[]>(() => {
    const currentCryptoAddresses = [];
    const otherAddresses = [];

    const filteredByCurrency = !currenciesStore.cryptoCurrencies?.length
        ? walletAddressesStore.walletAddresses
        : walletAddressesStore.walletAddresses.filter((wallet) => currenciesStore.cryptoCurrencies.includes(wallet.currency));

    const search = filter.value?.toUpperCase();
    const filteredBySearch = !search ? filteredByCurrency : filteredByCurrency.filter((wallet) => walletMatchesSearch(wallet, search));

    for (const wallet of filteredBySearch) {
        if (wallet.identifier !== walletAddressesStore.selectedWalletAddress?.identifier) {
            wallet.currency === currenciesStore.selectedCryptoCurrency.code ? currentCryptoAddresses.push(wallet) : otherAddresses.push(wallet);
        }
    }

    const result = [...currentCryptoAddresses, ...sortWalletsByCurrency(otherAddresses)];
    if (!walletAddressesStore.selectedWalletAddress) {
        return result;
    }

    return [walletAddressesStore.selectedWalletAddress, ...result];
});

const disabledWalletAddressIdentifiers = computed<string[]>(() => {
    const disabledCryptos = currenciesStore.disabledCurrencyPairs.reduce((result: string[], currency: CurrencyPair) => {
        if (isDisabled(currency, props.quoteType)) {
            result.push(currency.baseCurrency.code);
        }
        return result;
    }, []);

    const disabledWalletAddressIdentifiers = walletAddresses.value.reduce((result: string[], wallet: WalletAddress) => {
        if (disabledCryptos.includes(wallet.currency)) {
            result.push(wallet.identifier);
        }
        return result;
    }, []);

    return disabledWalletAddressIdentifiers;
});

function walletMatchesSearch(wallet: WalletAddress, search: string): boolean {
    return wallet.currency.toUpperCase().includes(search) || wallet.address.toUpperCase().includes(search) || !!wallet.name?.toUpperCase().includes(search);
}

function sortWalletsByCurrency(addresses: WalletAddress[]): WalletAddress[] {
    return addresses.sort((compare, to) => {
        if (compare.currency === to.currency) {
            return 0;
        }

        return compare.currency < to.currency ? -1 : 1;
    });
}

const append = ref<HTMLElement>();
const clicked = (event: PointerEvent, open: () => void): void => {
    if (!manualWalletAddressEnabled.value || (append.value && append.value.contains(<HTMLElement>event.target))) {
        return open();
    }

    walletAddressesStore.setCurrentWalletAddressType('manual');
};

const focusWalletAddressField = ref<boolean>(false);
const focusSearchField = ref<boolean>(false);

const selectorOpened = (): void => {
    focusWalletAddressField.value = false;

    setTimeout(() => {
        filter.value = previousFilter.value;
        previousFilter.value = null;
    }, 50);

    setTimeout(() => {
        focusSearchField.value = true;
    }, 550);
};

const selectorClosed = (): void => {
    focusSearchField.value = false;
    focusWalletAddressField.value = true;

    previousFilter.value = filter.value;
    filter.value = null;
};

const quickSelectExistingAddress = (event: KeyboardEvent): void => {
    if (manualWalletAddressEnabled.value) {
        return;
    }

    const letter = event.key.toUpperCase();
    if (!letter.match(/[A-Z]/g)) {
        return;
    }

    const found = walletAddresses.value?.filter((wallet) => wallet.currency.startsWith(letter));
    if (!found || !found.length) {
        return;
    }

    if (found.length === 1) {
        selectWalletAddress(found[0]);
        return;
    }

    const index = found.findIndex((wallet) => wallet.address === walletAddressesStore.selectedWalletAddress.address);
    if (index === found.length - 1) {
        selectWalletAddress(found[0]);
        return;
    }

    selectWalletAddress(found[index + 1]);
};

const selectedExistingAddress = (data: ValidatedStringArrayFieldData, close: () => void): void => {
    const found = walletAddresses.value.find((wallet) => wallet.identifier === data.value[0]) ?? null;

    selectWalletAddress(found);
    close();
};

const selectedManualAddress = (data: ValidatedStringFieldData): void => {
    if (!manualWalletAddressEnabled.value || currentWalletAddressType.value === 'select') {
        return;
    }

    triggerRequiredError.value = null;

    const existing = walletAddressesStore.walletAddresses.find(
        (wallet) => wallet.address === data.value && wallet.currency === currenciesStore.selectedCryptoCurrency.code
    );

    walletAddressesStore.setManualAddress(
        existing ?? {
            address: data.value,
            currency: currenciesStore.selectedCryptoCurrency.code,
            status: 'open'
        }
    );

    if (existing && existing.currency !== currenciesStore.selectedCryptoCurrency.code) {
        currenciesStore.setCryptoCurrency(existing.currency);
    }

    emit('updated', data);
};

const createdManualAddress = (data: ValidatedStringFieldData) => {
    if (!manualWalletAddressEnabled.value || currentWalletAddressType.value === 'select') {
        return;
    }

    emit('created', data);
};

function selectWalletAddress(wallet: WalletAddress): void {
    triggerRequiredError.value = null;

    walletAddressesStore.selectWalletAddress(wallet);

    emit('updated', {
        name: NAME,
        value: wallet.address,
        valid: true,
        failed: []
    });
}

const validations = computed<ValidationMethod[]>(() =>
    currentWalletAddressType.value === 'manual' ? [getWalletAddressValidation(currenciesStore.selectedCryptoCurrency.code)] : []
);

const triggerRequiredError = ref<string>(null);
const closeWalletSelect = (close: () => void) => {
    if (!!currentWalletAddress.value?.address) {
        triggerRequiredError.value = null;
        return close();
    }

    triggerRequiredError.value = 'required';
    close();
};

watch(showFieldErrors, (received: boolean) => {
    if (!received || !!currentWalletAddress.value?.address) {
        return;
    }

    triggerRequiredError.value = 'required';
});

onMounted(() => {
    if (walletAddressesStore.selectedWalletAddress && !isVerified(walletAddressesStore.selectedWalletAddress)) {
        walletAddressesStore.setManualAddress(walletAddressesStore.selectedWalletAddress);
    }

    if (currentWalletAddressType.value === 'manual') {
        return;
    }

    emit('created', {
        name: NAME,
        value: currentWalletAddress.value?.address ?? null,
        valid: isVerified(currentWalletAddress.value),
        failed: !currentWalletAddress.value?.address ? ['required'] : []
    });
});
</script>

<style lang="scss" scoped>
@use '@@scss/flex';

.wallet-address-field {
    &:not(.manual) {
        cursor: pointer;
        :deep(main.input:hover),
        :deep(input:hover) {
            cursor: pointer;
        }
    }

    &.manual {
        cursor: text;

        :deep(.append:hover) {
            cursor: pointer;
        }
    }

    :deep(.prepend) {
        .icon {
            padding-left: 0.875em;
            width: 2.875em;
        }
    }
}
</style>
